import { useToast } from '@/context/ToastContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { OrderSearchResult } from '@/api/models';
import type { OrderRefreshRate } from '@/library/OrderSettingsHelper';
import { findNewOrders, prependOrders } from '@/library/OrdersResultsHelper';
import { OfflineOrdersHelper } from '@/library';
import { OrdersService } from '@/service';
import useOnlineStatus from '@/hooks/useOnlineStatus';
import { OrdersPageType } from './OrdersPresentation';
import { buildSearchRequest, initialQuery, initialSortBy, ORDERS_PER_PAGE, type OrdersDashboardQuery } from './OrdersQuery';
import type { OrderSort } from './OrderSorting';
import { loadOrderDefaults } from './OrderDefaults';

interface OrdersTableState {
  orders: OrderSearchResult[];
  totalOrders: number;
  isLoading: boolean;
  loadFailed: boolean;
  highlightedIds: number[];
  knownIds: Set<number>;
  searchKey: string;
  requestKey: string;
}

export const useOrdersDashboard = (currentPage: OrdersPageType, refreshRate: OrderRefreshRate) => {
  const isOnline = useOnlineStatus();
  const { showToast } = useToast();
  const [query, setQuery] = useState<OrdersDashboardQuery>(initialQuery);
  const [defaultsLoading, setDefaultsLoading] = useState(true);
  const [sortBy, setSortBy] = useState<OrderSort>(initialSortBy);
  const [table, setTable] = useState<OrdersTableState>({
    orders: [],
    totalOrders: 0,
    isLoading: true,
    loadFailed: false,
    highlightedIds: [],
    knownIds: new Set<number>(),
    searchKey: '',
    requestKey: '',
  });
  const request = buildSearchRequest(query, sortBy, currentPage);
  // Store the current filter request to match before adding new orders to validate its still valid
  const requestKey = JSON.stringify({ isOnline, request });
  const filterRequest = { ...request };
  // Remove page num and sorting since we don't need them for query comparission
  delete filterRequest.pageNo;
  delete filterRequest.sortBy;
  const searchKey = JSON.stringify(filterRequest);
  // The interval needs the latest query without restarting whenever the user edits it.
  const latest = useRef({ query, sortBy, searchKey, isOnline, table });

  const changeSort = (nextSort: OrderSort) => {
    setSortBy(nextSort);
    setQuery((previous) => ({ ...previous, offset: 0 }));
  };
  const changePage = (offset: number) => setQuery((previous) => ({ ...previous, offset }));

  const applyDefaults = useCallback(async () => {
    setDefaultsLoading(true);
    try {
      const defaults = await loadOrderDefaults(currentPage);
      setQuery(defaults.query);
      if (defaults.hasMissingSelections) showToast({ message: 'A user default setting is no longer active. Available defaults have been applied.', status: 'info' });
      // Invalidate a completed request even when the defaults match the active query.
      setTable((previous) => ({ ...previous, requestKey: '' }));
    } catch (error) {
      showToast({ message: error instanceof Error ? error.message : 'Your search defaults could not be read. Please retry.', status: 'error' });
    } finally {
      setDefaultsLoading(false);
    }
  }, [currentPage, showToast]);

  useEffect(() => {
    void applyDefaults();
  }, [applyDefaults]);

  useEffect(() => {
    // Update the latest ref if any of the table data or filters change
    latest.current = { query, sortBy, searchKey, isOnline, table };
  }, [query, sortBy, searchKey, isOnline, table]);

  // Load the active query when filters, sorting, pagination, or connectivity change.
  useEffect(() => {
    if (defaultsLoading) return;
    if ((!isOnline || currentPage !== OrdersPageType.Clubs) && query.playerModels.length) {
      setQuery((previous) => ({ ...previous, playerModels: [], offset: 0 }));
      return;
    }
    // A poll can already supply the results for page 1 when it resets pagination.
    if (latest.current.table.requestKey === requestKey && !latest.current.table.isLoading) return;
    let active = true;
    setTable((previous) => ({
      ...previous,
      orders: [],
      isLoading: true,
      loadFailed: false,
      highlightedIds: [],
      knownIds: previous.searchKey === searchKey ? previous.knownIds : new Set(),
      searchKey,
      requestKey,
    }));
    const load = async () => {
      try {
        const search = isOnline ? OrdersService : OfflineOrdersHelper;
        const request = buildSearchRequest(query, sortBy, currentPage);
        if (!isOnline) delete request.playerModels;
        const response = await search.searchOrders(request);
        if (!active) return;
        setTable((previous) => {
          if (!previous.isLoading) return previous;
          return {
            ...previous,
            orders: response.results,
            totalOrders: response.searchRowCount,
            knownIds: new Set([...previous.knownIds, ...response.results.map((order) => order.orderMstrId)]),
            isLoading: false,
          };
        });
      } catch {
        if (active) setTable((previous) => (previous.isLoading ? { ...previous, orders: [], totalOrders: 0, isLoading: false, loadFailed: true } : previous));
      }
    };
    void load();
    return () => {
      active = false;
    };
  }, [query, sortBy, currentPage, isOnline, requestKey, searchKey, defaultsLoading]);

  // Poll the latest filters at the saved interval and discard results for changed filters.
  // New arrivals are highlighted and return the table to page 1.
  useEffect(() => {
    let fetching = false;
    const refresh = async () => {
      const snapshot = latest.current;
      if (!snapshot.isOnline || snapshot.table.isLoading || fetching) return;
      fetching = true;
      try {
        const request = buildSearchRequest({ ...snapshot.query, offset: 0 }, initialSortBy, currentPage);
        const response = await OrdersService.refreshOrders(request);
        const current = latest.current;
        // If the filters have changed or its no longer fetching return
        if (!fetching || snapshot.searchKey !== current.searchKey || !current.isOnline) return;
        const additions = findNewOrders(response.results, current.table.knownIds);
        // If there are no new orders we don't need to update the table
        if (!additions.length) return;
        const pageQuery = { ...current.query, offset: 0 };
        const pageKey = JSON.stringify({ isOnline: current.isOnline, request: buildSearchRequest(pageQuery, initialSortBy, currentPage) });

        setTable((previous) => {
          return {
            ...previous,
            orders: prependOrders(additions, response.results).slice(0, ORDERS_PER_PAGE),
            totalOrders: response.searchRowCount,
            knownIds: new Set([...previous.knownIds, ...response.results.map((order) => order.orderMstrId)]),
            highlightedIds: additions.map((order) => order.orderMstrId),
            loadFailed: false,
            isLoading: false,
            requestKey: pageKey,
          };
        });
        setSortBy(initialSortBy);
        if (current.query.offset !== 0) setQuery(pageQuery);
      } catch (error) {
        console.error('Could not refresh orders:', error);
      } finally {
        fetching = false;
      }
    };
    const timer = window.setInterval(() => void refresh(), refreshRate * 60_000);
    return () => {
      fetching = false;
      window.clearInterval(timer);
    };
  }, [currentPage, refreshRate]);

  return {
    query,
    sortBy,
    defaultsLoading,
    applyDefaults,
    orders: table.orders,
    totalOrders: table.totalOrders,
    isLoading: table.isLoading,
    loadFailed: table.loadFailed,
    highlightedIds: table.highlightedIds,
    changeFilters: setQuery,
    changeSort,
    changePage,
  };
};
